import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getEnumOptions } from '@/service/components';

/** 通知场景枚举 Tag 配色（与服务通知分组展示一致） */
const NOTIFICATION_SCENE_TAG_COLORS = [
  'blue',
  'orange',
  'green',
  'purple',
  'pink',
  'cyan',
  'geekblue',
  'magenta',
  'volcano',
  'gold'
];

// 与 getJStoreListSlice 的 processSingleDataType 同思路；枚举接口额外兼容 body.data 数组
const processSingleDataType = ({ data, dataType }) => {
  let list = data;
  if (list && !Array.isArray(list)) {
    if (Array.isArray(list.records)) {
      list = list.records;
    } else if (Array.isArray(list.data)) {
      list = list.data;
    }
  }
  if (!Array.isArray(list)) return [];

  if (!dataType || dataType === 'default') {
    return list;
  }

  switch (dataType) {
    case 'option':
      return list.map((item) => ({
        id: `${item.value}`,
        value: item.desc
      }));
    /** 与 option 同源（id/value），并按序附加 Tag color；配对 key=NotifyCategory */
    case 'notifyCategory':
      return list.map((item, idx) => ({
        id: `${item.value}`,
        value: item.desc,
        color: NOTIFICATION_SCENE_TAG_COLORS[idx % NOTIFICATION_SCENE_TAG_COLORS.length]
      }));
    default:
      return list;
  }
};

const initialState = {
  PermissionType: {
    data: [],
    query: {},
    fetching: false,
    error: null
  },
  /** GET /common/enum/options?key=DataScopeType（角色资源范围等） */
  DataScopeType: {
    data: [],
    query: {},
    fetching: false,
    error: null
  },
  /** GET /common/enum/options?key=NotifyCategory */
  NotifyCategory: {
    data: [],
    query: {},
    fetching: false,
    error: null
  },
  /** GET /common/enum/options?key=NotificationChannel */
  NotificationChannel: {
    data: [],
    query: {},
    fetching: false,
    error: null
  },
  /** GET /common/enum/options?key=NotificationTargetType（如 CUSTOMER、EMPLOYEE） */
  NotificationTargetType: {
    data: [],
    query: {},
    fetching: false,
    error: null
  },
  /** GET /common/enum/options?key=NotificationTriggerType */
  NotificationTriggerType: {
    data: [],
    query: {},
    fetching: false,
    error: null
  },
  /** GET /common/enum/options?key=NotificationOffsetUnit */
  NotificationOffsetUnit: {
    data: [],
    query: {},
    fetching: false,
    error: null
  },
  /** GET /common/enum/options?key=NotificationLifecycle */
  NotificationLifecycle: {
    data: [],
    query: {},
    fetching: false,
    error: null
  }
};

export const fetchEnumOptions = createAsyncThunk(
  'enumOptions/fetchEnumOptions',
  async ({ key, query = {}, dataType }, { rejectWithValue }) => {
    try {
      const response = await getEnumOptions({ key, ...query });
      return {
        data: response.data,
        query,
        key,
        dataType
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取枚举选项失败');
    }
  }
);

const enumOptionsSlice = createSlice({
  name: 'enumOptions',
  initialState,
  reducers: {
    resetEnumOptionsState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEnumOptions.pending, (state, action) => {
        const { key: k, query: argQuery = {} } = action.meta.arg;
        state[k].fetching = true;
        state[k].error = null;
        state[k].query = { key: k, ...argQuery };
        state[k].data = [];
      })
      .addCase(fetchEnumOptions.fulfilled, (state, action) => {
        const { data, dataType, query, key } = action.payload;
        state[key].fetching = false;
        state[key].error = null;
        state[key].query = { key, ...(query || {}) };
        state[key].data = processSingleDataType({ data, dataType });
      })
      .addCase(fetchEnumOptions.rejected, (state, action) => {
        const { key: k } = action.meta.arg;
        state[k].fetching = false;
        state[k].error = action.payload;
        state[k].data = [];
      });
  }
});

export const { resetEnumOptionsState } = enumOptionsSlice.actions;
export default enumOptionsSlice.reducer;
